import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, LogOut, Lock, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/data";
import { ReferralsAdmin } from "@/components/ReferralsAdmin";
import { ProjectsAdmin } from "@/components/realestate/ProjectsAdmin";

const COLS = {
  leads: [["client_name", "Name"], ["client_phone", "Phone"], ["target_partner_name", "Partner / Product"], ["target_partner_id", "Source"], ["callback_time", "Callback"], ["timestamp", "Time"]],
  club: [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["city", "City"], ["state", "State"], ["verticals", "Interests"], ["timestamp", "Joined"]],
  whatsapp: [["timestamp", "Time"], ["to", "To"], ["kind", "Type"], ["status", "Status"], ["body", "Message"]],
  clicks: [["timestamp", "Time"], ["provider_name", "Provider"], ["provider_id", "ID"], ["src", "Source"], ["referral", "Referral"]],
  affiliates: [["full_name", "Name"], ["entity_name", "Entity"], ["contact_mobile", "Mobile"], ["operating_segment", "Segment"], ["registration_number", "Reg. No."], ["timestamp", "Time"]],
};

const SRC = { "b-": "Stock Broker", "i-": "Insurer", "x-": "Crypto Exchange", "c-": "Commodity Broker", "f-": "Forex Broker", "mf": "Mutual Fund", "re": "Real Estate", p: "Partner (legacy)" };
const fmt = (k, v) => (k === "referral" ? (v ? "Yes" : "No") : k === "timestamp" ? new Date(v).toLocaleString("en-IN") : k === "target_partner_id" ? `${SRC[v.slice(0, 2)] || SRC[v[0]] || "—"} (${v})` : v);

const Login = ({ onToken }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/admin/login", { password: pw });
      sessionStorage.setItem("dt_admin", data.token);
      toast.dismiss();
      onToken(data.token);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#1E293B] flex items-center justify-center p-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-white rounded-2xl p-7 shadow-2xl">
        <div className="bg-[#1E293B] rounded-xl p-3 inline-block"><img src="/logo.png" alt="DhanTrove" className="h-7" /></div>
        <h1 className="font-display text-2xl font-bold mt-5 flex items-center gap-2"><Lock className="w-5 h-5" /> Admin Access</h1>
        <input data-testid="admin-passkey-input" type="password" required placeholder="Admin password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-5 w-full h-12 rounded-xl border border-slate-200 px-4 outline-none focus:ring-2 focus:ring-[#10B981]" />
        <button data-testid="admin-login-button" disabled={loading} className="mt-3 w-full h-12 rounded-xl bg-[#10B981] hover:bg-[#059669] text-white font-bold inline-flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Enter Dashboard
        </button>
      </form>
    </div>
  );
};

const DataTable = ({ kind, token, onExpired }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const { data = [], isLoading, error } = useQuery({ queryKey: ["admin", kind, token], queryFn: () => http.get(`/admin/${kind}`, { headers }).then((r) => r.data), retry: false });
  useEffect(() => { if (error?.response?.status === 401) onExpired(); }, [error, onExpired]);

  const exportCsv = async () => {
    try {
      const res = await http.get(`/admin/export/${kind}`, { headers, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `dhantrove_${kind}.csv`;
      a.click();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 mt-4">
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <p data-testid={`admin-${kind}-count`} className="font-semibold">{data.length} records</p>
        <button data-testid={`admin-export-${kind}-button`} onClick={exportCsv} className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#10B981] hover:bg-[#059669] text-white text-sm font-bold">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid={`admin-${kind}-table`} className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>{COLS[kind].map(([, l]) => <th key={l} className="px-4 py-3 font-semibold whitespace-nowrap">{l}</th>)}</tr>
          </thead>
          <tbody>
            {isLoading && <tr><td className="px-4 py-6 text-slate-400" colSpan={6}>Loading…</td></tr>}
            {!isLoading && data.length === 0 && <tr><td className="px-4 py-6 text-slate-400" colSpan={6}>No records yet</td></tr>}
            {data.map((r, i) => (
              <tr key={r.lead_id || r.registration_id || r.member_id || `${r.timestamp}|${r.to}|${i}`} className="border-t border-slate-100">
                {COLS[kind].map(([k]) => <td key={k} className={`px-4 py-3 ${k === "body" ? "min-w-[320px] whitespace-pre-line" : "whitespace-nowrap"}`}>{fmt(k, r[k])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("dt_admin"));
  const logout = () => { sessionStorage.removeItem("dt_admin"); setToken(null); };
  if (!token) return <Login onToken={setToken} />;
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#1E293B] px-4 sm:px-6 py-4 flex items-center justify-between">
        <img src="/logo.png" alt="DhanTrove" className="h-8" />
        <button data-testid="admin-logout-button" onClick={logout} className="text-slate-300 hover:text-white text-sm inline-flex items-center gap-1.5"><LogOut className="w-4 h-4" /> Logout</button>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold">Leads Dashboard</h1>
        <Tabs defaultValue="leads" className="mt-6">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Investor Leads</TabsTrigger>
            <TabsTrigger value="affiliates" data-testid="admin-tab-affiliates">Partner Registrations</TabsTrigger>
            <TabsTrigger value="projects" data-testid="admin-tab-projects">Property Listings</TabsTrigger>
            <TabsTrigger value="club" data-testid="admin-tab-club">IRIE Club</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="admin-tab-whatsapp">WhatsApp Log</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="admin-tab-referrals">Referral Links</TabsTrigger>
            <TabsTrigger value="clicks" data-testid="admin-tab-clicks">Click Log</TabsTrigger>
          </TabsList>
          <TabsContent value="leads"><DataTable kind="leads" token={token} onExpired={logout} /></TabsContent>
          <TabsContent value="affiliates"><DataTable kind="affiliates" token={token} onExpired={logout} /></TabsContent>
          <TabsContent value="projects"><ProjectsAdmin token={token} /></TabsContent>
          <TabsContent value="club"><DataTable kind="club" token={token} onExpired={logout} /></TabsContent>
          <TabsContent value="whatsapp"><DataTable kind="whatsapp" token={token} onExpired={logout} /></TabsContent>
          <TabsContent value="referrals"><ReferralsAdmin token={token} onExpired={logout} /></TabsContent>
          <TabsContent value="clicks"><DataTable kind="clicks" token={token} onExpired={logout} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
